import { useState, useMemo } from 'react'
import katex from 'katex'

function MathFormula({ formula, displayMode = false, className = '' }: { formula: string; displayMode?: boolean; className?: string }) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(formula, {
        displayMode,
        throwOnError: false,
      })
    } catch {
      return formula
    }
  }, [formula, displayMode])

  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

export function PremiumFeaturesSection() {
  const [activeMethod, setActiveMethod] = useState<number>(1) // Default: Method 2 Speed Shortcut

  // Actual Solution Methods matching PrepKiya StructuredSolution Architecture
  const methods = [
    {
      id: 0,
      badge: 'Method 1',
      title: 'Derivation',
      fullName: 'Rigorous First-Principles Derivation',
      time: '~60 sec',
      steps: [
        { label: 'Step 1: Set up the integral equation', math: 'I = \\int_{0}^{\\pi/2} \\frac{\\sin x}{\\sin x + \\cos x} \\, dx \\quad \\text{--- (1)}' },
        { label: "Step 2: Apply King's Property: \\int_{a}^{b} f(x)dx = \\int_{a}^{b} f(a+b-x)dx", math: 'I = \\int_{0}^{\\pi/2} \\frac{\\cos x}{\\cos x + \\sin x} \\, dx \\quad \\text{--- (2)}' },
        { label: 'Step 3: Add equations (1) and (2)', math: '2I = \\int_{0}^{\\pi/2} 1 \\, dx = \\frac{\\pi}{2}' },
        { label: 'Step 4: Solve for I', math: 'I = \\frac{\\pi}{4}' },
      ],
    },
    {
      id: 1,
      badge: 'Method 2',
      title: 'Speed Shortcut',
      fullName: '10-Second Exam Hall Speed Shortcut',
      time: '< 5 sec',
      steps: [
        { label: 'Step 1: Recognize complementary symmetric integrand', math: 'f(x) + f(a + b - x) = 1 \\quad \\text{on } [0, \\pi/2]' },
        { label: 'Step 2: Apply direct definite integral shortcut formula', math: 'I = \\frac{b - a}{2} = \\frac{\\pi/2 - 0}{2} = \\frac{\\pi}{4}' },
        { label: 'Step 3: Direct option mark in under 5 seconds', math: 'I = \\frac{\\pi}{4}' },
      ],
    },
    {
      id: 2,
      badge: 'Method 3',
      title: 'Boundary Check',
      fullName: 'Case / Boundary Sanity Check',
      time: '~20 sec',
      steps: [
        { label: 'Step 1: Symmetry midpoint evaluation at x = \\pi/4', math: 'f(\\pi/4) = \\frac{\\sin(\\pi/4)}{\\sin(\\pi/4) + \\cos(\\pi/4)} = \\frac{1/\\sqrt{2}}{2/\\sqrt{2}} = \\frac{1}{2}' },
        { label: 'Step 2: Bounding box average value theorem', math: 'I = \\text{Midpoint Height} \\times (b - a) = \\frac{1}{2} \\times \\left(\\frac{\\pi}{2} - 0\\right) = \\frac{\\pi}{4}' },
        { label: 'Step 3: Bounds consistency check', math: '0 < I < \\frac{\\pi}{2} \\implies I = \\frac{\\pi}{4} \\approx 0.7854' },
      ],
    },
    {
      id: 3,
      badge: 'Method 4',
      title: 'Visual Diagram',
      fullName: 'Visual & Diagrammatic Solution',
      time: '~15 sec',
      steps: [
        { label: 'Step 1: Area splitting under complementary curves', math: 'y_1 = \\frac{\\sin x}{\\sin x + \\cos x}, \\quad y_2 = \\frac{\\cos x}{\\sin x + \\cos x}' },
        { label: 'Step 2: Geometric reflection about line x = \\pi/4', math: 'y_1 + y_2 = 1 \\implies \\text{Area}(y_1) = \\text{Area}(y_2)' },
        { label: 'Step 3: Total rectangle area of [0, \\pi/2] \\times [0, 1]', math: 'I = \\frac{1}{2} \\times \\left(1 \\times \\frac{\\pi}{2}\\right) = \\frac{\\pi}{4}' },
      ],
    },
  ]

  const currentMethod = methods[activeMethod]

  return (
    <section className="w-full py-20 sm:py-28 bg-[#131b2e] text-white">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <h2 className="!font-sans text-3xl sm:text-4xl lg:text-[44px] font-black text-white tracking-tight">
            Elevate Your Preparation with PrepKiya Premium
          </h2>
        </div>

        {/* 2-Card Grid with Matching Symmetrical Height */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          
          {/* Card 1: Multiple Solution Pathways */}
          <div className="bg-white text-slate-900 rounded-3xl p-6 sm:p-8 flex flex-col justify-between shadow-2xl border border-slate-100 h-full">
            <div className="flex-1 flex flex-col justify-between space-y-6">
              
              {/* Header */}
              <div className="h-14 flex flex-col justify-center">
                <span className="!font-sans text-xs sm:text-sm font-semibold text-slate-500 block">
                  4 Solution Methods.
                </span>
                <h3 className="!font-sans text-2xl sm:text-3xl font-black text-[#131b2e] tracking-tight">
                  Multiple Solution Pathways
                </h3>
              </div>

              {/* Mockup Question UI Container */}
              <div className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-4 shadow-2xs">
                
                {/* Question Box */}
                <div className="bg-white border border-slate-200/90 rounded-xl p-4 space-y-3 shadow-3xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="!font-sans font-bold text-xs text-[#131b2e]">Q.48</span>
                      <span className="!font-sans text-2xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 border border-blue-100">
                        Math · Calculus
                      </span>
                    </div>
                    <span className="!font-sans text-2xs font-bold text-slate-500">
                      +4 / -1 Marks
                    </span>
                  </div>

                  <p className="!font-sans text-xs text-slate-700 font-medium leading-relaxed">
                    The value of the definite integral is:
                  </p>

                  {/* KaTeX Math Display */}
                  <div className="py-2.5 px-4 bg-slate-50 rounded-xl border border-slate-200/80 text-center select-none overflow-x-auto flex items-center justify-center">
                    <MathFormula formula="I = \int_{0}^{\pi/2} \frac{\sin x}{\sin x + \cos x} \, dx" displayMode={true} />
                  </div>
                </div>

                {/* 4 Method Selector Buttons */}
                <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                  {methods.map((m) => {
                    const isSelected = activeMethod === m.id
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setActiveMethod(m.id)}
                        className={`!font-sans py-2 px-1 sm:px-2 rounded-xl text-center font-bold transition-all border cursor-pointer flex flex-col items-center justify-center min-h-[48px] w-full ${
                          isSelected
                            ? 'bg-[#c8933b] text-white border-[#b5802a] shadow-sm'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <span className="text-[10px] font-extrabold uppercase tracking-tight block leading-tight opacity-90 truncate w-full">
                          {m.badge}
                        </span>
                        <span className="text-[11px] font-black block leading-tight mt-0.5 truncate w-full">
                          {m.title}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {/* Step-by-step logic box */}
                <div className="bg-white border border-slate-200/90 rounded-xl p-4 space-y-2.5 flex-1 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                    <span className="!font-sans text-xs font-bold text-slate-800 truncate">
                      {currentMethod.fullName}:
                    </span>
                    <span className="text-3xs font-semibold text-emerald-600 font-mono shrink-0 ml-2">
                      Speed: {currentMethod.time}
                    </span>
                  </div>

                  <div className="space-y-2 text-xs text-slate-700 font-medium">
                    {currentMethod.steps.map((step, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center gap-1.5 font-bold text-slate-800 text-2xs">
                          <span className="text-[#c8933b] font-black">{idx + 1}.</span>
                          <span>{step.label}</span>
                        </div>
                        <div className="pl-4 text-xs text-slate-900 overflow-x-auto py-0.5">
                          <MathFormula formula={step.math} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* Bottom text */}
              <p className="!font-sans text-sm text-slate-600 leading-relaxed font-normal pt-1">
                Analyze problems through 4 different comprehensive methods.
              </p>

            </div>
          </div>

          {/* Card 2: Topper High-Yield Notes */}
          <div className="bg-white text-slate-900 rounded-3xl p-6 sm:p-8 flex flex-col justify-between shadow-2xl border border-slate-100 h-full">
            <div className="flex-1 flex flex-col justify-between space-y-6">
              
              {/* Header with Clean Badge (Matching height with Card 1) */}
              <div className="h-14 flex items-center justify-between">
                <div>
                  <span className="!font-sans text-xs sm:text-sm font-semibold text-slate-500 block">
                    Topper Notes.
                  </span>
                  <h3 className="!font-sans text-2xl sm:text-3xl font-black text-[#131b2e] tracking-tight">
                    Exams Topper High-Yield Notes
                  </h3>
                </div>

                {/* Clean Rosette Seal */}
                <div className="shrink-0 flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full shadow-2xs">
                  <div className="w-5 h-5 rounded-full bg-[#c8933b] text-white flex items-center justify-center font-black text-3xs">
                    ★
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-3xs font-black text-amber-900 leading-none">RANK 1</span>
                    <span className="text-3xs font-bold text-amber-700 leading-none">TOPPER NOTES</span>
                  </div>
                </div>
              </div>

              {/* Mockup Open Ruled Notebook Container (Fills equal height with Card 1) */}
              <div className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-4 sm:p-6 flex-1 flex items-center justify-center shadow-2xs overflow-hidden">
                
                {/* Open Notebook Graphic */}
                <div className="relative w-full max-w-md bg-white border-4 border-[#131b2e] rounded-2xl shadow-xl flex overflow-hidden my-auto">
                  
                  {/* Left Ruled Page */}
                  <div className="w-1/2 p-4 pr-3 border-r-2 border-slate-200 space-y-3.5 select-none bg-white">
                    <div className="border-b border-amber-300/80 pb-1">
                      <span className="text-3xs font-black text-[#131b2e] uppercase tracking-wider block">Formula Solutions</span>
                      <p className="text-3xs text-slate-500 italic font-serif">crucial problems</p>
                    </div>

                    <div className="space-y-1 bg-amber-50/70 p-2.5 rounded-lg border border-amber-200/60">
                      <span className="text-3xs font-bold text-amber-900 block">Formulas</span>
                      <div className="text-3xs text-slate-900 space-y-1">
                        <div><MathFormula formula="v = u + at" /></div>
                        <div><MathFormula formula="s = ut + \frac{1}{2}at^2" /></div>
                        <div><MathFormula formula="v^2 = u^2 + 2as" /></div>
                      </div>
                    </div>

                    <div className="space-y-1 pt-1">
                      <span className="text-3xs font-bold text-slate-700 block">Diagrams</span>
                      <svg viewBox="0 0 100 35" className="w-full h-8 overflow-visible">
                        <line x1="5" y1="30" x2="95" y2="30" stroke="#0F172A" strokeWidth="1.5" />
                        <line x1="5" y1="30" x2="5" y2="5" stroke="#0F172A" strokeWidth="1.5" />
                        <path d="M5 30 Q50 5 95 30" fill="none" stroke="#F59E0B" strokeWidth="2" />
                      </svg>
                      <span className="text-3xs text-slate-500 block text-center font-mono">Kinematics Curve</span>
                    </div>
                  </div>

                  {/* Spiral Rings */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 flex flex-col justify-around py-3 z-10">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="w-3.5 h-2 bg-[#131b2e] rounded-full border border-slate-300 shadow-xs" />
                    ))}
                  </div>

                  {/* Right Ruled Page */}
                  <div className="w-1/2 p-4 pl-4 space-y-3.5 select-none bg-white">
                    <div className="border-b border-amber-300/80 pb-1">
                      <span className="text-3xs font-extrabold text-[#131b2e] underline decoration-[#c8933b] uppercase tracking-wider block">Kinematics</span>
                      <div className="text-3xs font-mono font-bold text-slate-700">
                        <MathFormula formula="V_s = \frac{m_2 g}{b}" />
                      </div>
                    </div>

                    <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 space-y-1">
                      <span className="text-3xs font-bold text-slate-800 block">Newton&apos;s Laws</span>
                      <div className="text-3xs text-slate-900 space-y-1">
                        <div><MathFormula formula="F_{\text{net}} = m \cdot a" /></div>
                        <div><MathFormula formula="p = m \cdot v" /></div>
                      </div>
                    </div>

                    <div className="space-y-1 pt-1">
                      <div className="bg-amber-100 text-amber-950 px-2.5 py-0.5 rounded text-3xs font-extrabold inline-block border border-amber-300">
                        ★ Gold highlights
                      </div>
                      <p className="text-3xs text-slate-600 italic">High-yield notes</p>
                    </div>
                  </div>

                  {/* Stylus Pen */}
                  <div className="absolute -bottom-1 -right-1 transform -rotate-45 z-20 select-none drop-shadow-md">
                    <div className="w-4 h-24 bg-white border-2 border-[#131b2e] rounded-full flex flex-col items-center justify-between p-0.5">
                      <div className="w-2 h-3 bg-slate-300 rounded-t-full" />
                      <div className="w-1.5 h-12 bg-slate-100" />
                      <div className="w-2 h-4 bg-[#c8933b] rounded-b-full" />
                    </div>
                  </div>

                </div>

              </div>

              {/* Bottom text */}
              <p className="!font-sans text-sm text-slate-600 leading-relaxed font-normal pt-1">
                Unlock curated, high-yield handwritten-styled notes with essential formulas and visual derivations.
              </p>

            </div>
          </div>

        </div>

      </div>
    </section>
  )
}
